import { Point, Shape } from "../RampCanvasV2/RampCanvasV2";
import {
  BoundingBox,
  HANDLE_CURSORS,
  HANDLE_SIZE,
  HandleType,
  ROTATION_HANDLE_OFFSET,
  ROTATION_HANDLE_SIZE,
  TransformHandle,
} from "./types";

/**
 * Calculate bounding box for a set of shapes
 */
export function getBoundingBox(shapes: Shape[]): BoundingBox | null {
  if (shapes.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const shape of shapes) {
    for (const point of shape.points) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (!isFinite(minX) || !isFinite(minY)) return null;

  const width = maxX - minX;
  const height = maxY - minY;

  return {
    minX,
    minY,
    maxX,
    maxY,
    centerX: minX + width / 2,
    centerY: minY + height / 2,
    width,
    height,
  };
}

/**
 * Get adaptive handle size based on bounding box and zoom
 * Handles get slightly larger for small shapes
 */
export function getAdaptiveHandleSize(
  bounds: BoundingBox,
  zoomLevel: number
): number {
  const baseSize = HANDLE_SIZE / zoomLevel;
  const minDimension = Math.min(bounds.width, bounds.height);

  // For very small shapes, use smaller handles
  if (minDimension < 20) {
    return Math.max(4 / zoomLevel, baseSize * 0.6);
  }

  return baseSize;
}

/**
 * Generate all transform handles for a bounding box
 */
export function getTransformHandles(
  bounds: BoundingBox,
  zoomLevel: number
): TransformHandle[] {
  const size = getAdaptiveHandleSize(bounds, zoomLevel);
  const halfSize = size / 2;

  const handles: TransformHandle[] = [
    // Corner handles
    {
      type: "corner-nw",
      x: bounds.minX - halfSize,
      y: bounds.minY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["corner-nw"],
    },
    {
      type: "corner-ne",
      x: bounds.maxX - halfSize,
      y: bounds.minY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["corner-ne"],
    },
    {
      type: "corner-se",
      x: bounds.maxX - halfSize,
      y: bounds.maxY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["corner-se"],
    },
    {
      type: "corner-sw",
      x: bounds.minX - halfSize,
      y: bounds.maxY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["corner-sw"],
    },
    // Edge handles (centered on each edge)
    {
      type: "edge-n",
      x: bounds.centerX - halfSize,
      y: bounds.minY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["edge-n"],
    },
    {
      type: "edge-e",
      x: bounds.maxX - halfSize,
      y: bounds.centerY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["edge-e"],
    },
    {
      type: "edge-s",
      x: bounds.centerX - halfSize,
      y: bounds.maxY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["edge-s"],
    },
    {
      type: "edge-w",
      x: bounds.minX - halfSize,
      y: bounds.centerY - halfSize,
      width: size,
      height: size,
      cursor: HANDLE_CURSORS["edge-w"],
    },
    // Rotation handle (above the top center) - uses larger size for easier interaction
    {
      type: "rotate",
      x: bounds.centerX - ROTATION_HANDLE_SIZE / zoomLevel / 2,
      y: bounds.minY - ROTATION_HANDLE_OFFSET / zoomLevel - ROTATION_HANDLE_SIZE / zoomLevel / 2,
      width: ROTATION_HANDLE_SIZE / zoomLevel,
      height: ROTATION_HANDLE_SIZE / zoomLevel,
      cursor: HANDLE_CURSORS["rotate"],
    },
  ];

  return handles;
}

/**
 * Check if a point is inside a handle
 */
export function isPointInHandle(point: Point, handle: TransformHandle): boolean {
  return (
    point.x >= handle.x &&
    point.x <= handle.x + handle.width &&
    point.y >= handle.y &&
    point.y <= handle.y + handle.height
  );
}

/**
 * Find which handle (if any) is at the given position
 */
export function getHandleAtPosition(
  mousePos: Point,
  handles: TransformHandle[]
): TransformHandle | null {
  // Check in reverse order so rotation handle (last) is checked first
  for (let i = handles.length - 1; i >= 0; i--) {
    if (isPointInHandle(mousePos, handles[i])) {
      return handles[i];
    }
  }
  return null;
}

/**
 * Scale points from a corner anchor
 */
export function scaleFromCorner(
  points: Point[],
  anchor: Point,
  originalBounds: BoundingBox,
  newPos: Point,
  startPos: Point,
  maintainAspect: boolean = true
): Point[] {
  // Calculate the distance from anchor to current mouse vs anchor to start mouse
  // This gives us a more intuitive scale factor
  const startDistX = Math.abs(startPos.x - anchor.x);
  const startDistY = Math.abs(startPos.y - anchor.y);
  const currentDistX = Math.abs(newPos.x - anchor.x);
  const currentDistY = Math.abs(newPos.y - anchor.y);

  // Calculate scale factors based on distance ratio
  let scaleX = startDistX > 1 ? currentDistX / startDistX : 1;
  let scaleY = startDistY > 1 ? currentDistY / startDistY : 1;

  // Maintain aspect ratio if requested
  if (maintainAspect) {
    // Use average scale for proportional scaling
    const scale = (scaleX + scaleY) / 2;
    scaleX = scale;
    scaleY = scale;
  }

  // Prevent negative or zero scale
  scaleX = Math.max(0.1, scaleX);
  scaleY = Math.max(0.1, scaleY);

  return points.map((p) => ({
    x: anchor.x + (p.x - anchor.x) * scaleX,
    y: anchor.y + (p.y - anchor.y) * scaleY,
  }));
}

/**
 * Scale points from an edge (single axis)
 */
export function scaleFromEdge(
  points: Point[],
  edge: "n" | "e" | "s" | "w",
  originalBounds: BoundingBox,
  newPos: Point,
  startPos: Point
): Point[] {
  const isHorizontal = edge === "e" || edge === "w";
  const originalSize = isHorizontal ? originalBounds.width : originalBounds.height;

  let anchor: Point;
  let delta: number;

  switch (edge) {
    case "n":
      anchor = { x: originalBounds.centerX, y: originalBounds.maxY };
      delta = startPos.y - newPos.y;
      break;
    case "s":
      anchor = { x: originalBounds.centerX, y: originalBounds.minY };
      delta = newPos.y - startPos.y;
      break;
    case "e":
      anchor = { x: originalBounds.minX, y: originalBounds.centerY };
      delta = newPos.x - startPos.x;
      break;
    case "w":
      anchor = { x: originalBounds.maxX, y: originalBounds.centerY };
      delta = startPos.x - newPos.x;
      break;
  }

  const scale = Math.max(0.1, 1 + delta / (originalSize || 1));

  return points.map((p) => ({
    x: isHorizontal ? anchor.x + (p.x - anchor.x) * scale : p.x,
    y: !isHorizontal ? anchor.y + (p.y - anchor.y) * scale : p.y,
  }));
}

/**
 * Rotate points around a center
 */
export function rotateAroundCenter(
  points: Point[],
  center: Point,
  angleDelta: number
): Point[] {
  const cos = Math.cos(angleDelta);
  const sin = Math.sin(angleDelta);

  return points.map((p) => {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    return {
      x: center.x + dx * cos - dy * sin,
      y: center.y + dx * sin + dy * cos,
    };
  });
}

/**
 * Calculate angle from center to a point
 */
export function getAngleFromCenter(center: Point, point: Point): number {
  return Math.atan2(point.y - center.y, point.x - center.x);
}

/**
 * Check if a point is near any vertex of the given shapes.
 * Used to give vertex selection priority over transform handles.
 */
export function isNearVertexPoint(
  mousePos: Point,
  shapes: Shape[],
  threshold: number = 10
): boolean {
  for (const shape of shapes) {
    if (shape.locked || shape.type === "Spot") continue;
    for (const point of shape.points) {
      if (Math.hypot(point.x - mousePos.x, point.y - mousePos.y) < threshold) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Get the anchor point for a corner handle (opposite corner)
 */
export function getCornerAnchor(
  handleType: HandleType,
  bounds: BoundingBox
): Point {
  switch (handleType) {
    case "corner-nw":
      return { x: bounds.maxX, y: bounds.maxY };
    case "corner-ne":
      return { x: bounds.minX, y: bounds.maxY };
    case "corner-se":
      return { x: bounds.minX, y: bounds.minY };
    case "corner-sw":
      return { x: bounds.maxX, y: bounds.minY };
    default:
      return { x: bounds.centerX, y: bounds.centerY };
  }
}
